import React, { useCallback, useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { supabase } from '../lib/supabase';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { TrendingUp, Coins, Video, FileText, AlertTriangle, ArrowRight, Clock, Sparkles, Compass, BarChart3 } from 'lucide-react';
import {
  EmptyState,
  ErrorState,
  SkeletonHeader,
  SkeletonList,
  SkeletonPanel,
  SkeletonRegion,
  SkeletonStats,
} from '../components/ui/PageState';
import { Link } from 'react-router-dom';
import { Database } from '../types/database.types';
import { jobStatusLabel, listActiveJobs } from '../lib/jobsApi';
import type { JobRow } from '../types/jobs';
import { hasActiveStripeSub } from '../lib/billing';

type Generation = Database['public']['Tables']['generations']['Row'];

interface DailyUsage {
  date: string;
  credits: number;
}

interface DashboardStats {
  availableCredits: number;
  usedCreditsMonth: number;
  totalGenerations: number;
  pendingRequests: number;
  recentGenerations: Generation[];
  dailyUsage: DailyUsage[];
  activeJobs: JobRow[];
}

const CHART_DAYS = 30;
const LOW_CREDITS_THRESHOLD = 5;

/** Local calendar day key YYYY-MM-DD (avoids locale/DST mismatches). */
function toDayKey(date: Date): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function dayKeyLabel(key: string): string {
  const [, m, d] = key.split('-');
  return `${d}/${m}`;
}

/** Debit types used for AI / marketplace spend (exclude purchases & bonuses). */
const SPEND_TYPES = new Set([
  'generation_cost',
  'marketplace_purchase',
  'adjustment',
]);

function formatCredits(n: number): string {
  const v = Number.isFinite(n) ? n : 0;
  // Avoid float noise like 7840.440000000001 while keeping up to 2 decimals when needed.
  const rounded = Math.round(v * 100) / 100;
  return rounded.toLocaleString('pt-BR', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });
}

function isSpendTransaction(t: { amount: number; type: string }): boolean {
  if (t.amount >= 0) return false;
  // Prefer known spend types; fall back to any negative amount that isn't an inflow label
  if (SPEND_TYPES.has(t.type)) return true;
  if (t.type === 'purchase' || t.type === 'referral_bonus' || t.type === 'plan_renewal' || t.type === 'marketplace_sale') {
    return false;
  }
  // Unknown debit types (e.g. legacy withdraw_credits variants) still count as spend
  return true;
}

export default function Dashboard() {
  const { user } = useAuth();
  const { org, role, refreshOrg } = useOrg();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  useEffect(() => {
    refreshOrg(false);
  }, [refreshOrg]);

  const fetchStats = useCallback(async () => {
    if (!user || !org) return;
    setLoading(true);
    setLoadError(null);
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      startOfMonth.setHours(0, 0, 0, 0);
      const chartStart = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      chartStart.setDate(chartStart.getDate() - (CHART_DAYS - 1));
      chartStart.setHours(0, 0, 0, 0);

      const [genCountRes, requestsRes, recentGensRes, txnsRes] = await Promise.all([
        supabase
          .from('generations')
          .select('id', { count: 'exact', head: true })
          .eq('org_id', org!.id),
        supabase
          .from('requests')
          .select('id', { count: 'exact', head: true })
          .eq('org_id', org!.id)
          .eq('status', 'pending'),
        supabase
          .from('generations')
          .select('*')
          .eq('org_id', org!.id)
          .order('created_at', { ascending: false })
          .limit(5),
        // All org transactions in chart window (filter spend client-side — type names vary)
        supabase
          .from('credit_transactions')
          .select('amount, created_at, type')
          .eq('org_id', org!.id)
          .gte('created_at', chartStart.toISOString())
          .order('created_at', { ascending: true }),
      ]);

      if (txnsRes.error) {
        console.error('Dashboard credit_transactions error:', txnsRes.error);
      }
      if (genCountRes.error || recentGensRes.error) {
        throw genCountRes.error || recentGensRes.error;
      }

      const txns = txnsRes.data || [];
      const spendTxns = txns.filter(isSpendTransaction);

      const usedCredits = spendTxns
        .filter((t) => t.created_at && new Date(t.created_at) >= startOfMonth)
        .reduce((sum, t) => sum + Math.abs(Number(t.amount) || 0), 0);

      // Build daily chart buckets for last CHART_DAYS calendar days
      const dailyMap: Record<string, number> = {};
      for (let i = 0; i < CHART_DAYS; i++) {
        const d = new Date(chartStart);
        d.setDate(chartStart.getDate() + i);
        dailyMap[toDayKey(d)] = 0;
      }
      spendTxns.forEach((t) => {
        if (!t.created_at) return;
        const key = toDayKey(new Date(t.created_at));
        if (dailyMap[key] !== undefined) {
          dailyMap[key] += Math.abs(Number(t.amount) || 0);
        }
      });
      const dailyUsage = Object.entries(dailyMap).map(([key, credits]) => ({
        date: dayKeyLabel(key),
        credits: Math.round(credits * 100) / 100,
      }));

      let activeJobs: JobRow[] = [];
      try {
        activeJobs = await listActiveJobs(org!.id, 8);
      } catch (jobsErr) {
        console.error('Dashboard jobs error:', jobsErr);
      }

      setStats({
        availableCredits: Number(org!.available_credits) || 0,
        usedCreditsMonth: Math.round(usedCredits * 100) / 100,
        totalGenerations: genCountRes.count ?? 0,
        pendingRequests: requestsRes.count ?? 0,
        recentGenerations: recentGensRes.data || [],
        dailyUsage,
        activeJobs,
      });
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      setLoadError(err instanceof Error ? err.message : 'Falha ao consultar os dados.');
    } finally {
      setLoading(false);
    }
  }, [user, org]);

  useEffect(() => {
    void fetchStats();
  }, [fetchStats]);

  if (loadError && !stats) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Dashboard</h1>
          <p className="subtitle">Visão geral da sua organização</p>
        </div>
        <ErrorState
          title="Não foi possível carregar o dashboard"
          message="Seus créditos e gerações estão seguros. Tente carregar os números de novo."
          detail={loadError}
          onRetry={() => void fetchStats()}
          retrying={loading}
        />
      </div>
    );
  }

  if (loading || !stats) {
    return (
      <div className="page-container">
        <SkeletonRegion label="Carregando o dashboard…">
          <SkeletonHeader />
          <SkeletonStats count={4} />
          <SkeletonPanel height={260} />
          <div className="sk-panel"><SkeletonList rows={4} /></div>
        </SkeletonRegion>
      </div>
    );
  }

  const isLowCredits = stats.availableCredits < LOW_CREDITS_THRESHOLD;
  const isAdminOrFinOps = role === 'admin' || role === 'fin_ops';
  const totalChartCredits = stats.dailyUsage.reduce((sum, d) => sum + d.credits, 0);

  return (
    <div className="page-container">
      <div className="page-header">
        <div>
          <h1>Dashboard</h1>
          <p className="subtitle">Visão geral da sua organização</p>
        </div>
        <Link to="/painel-criacao" className="explore-btn is-primary">
          <Sparkles size={16} aria-hidden /> Criar com IA
        </Link>
      </div>

      {isLowCredits && (
        <div className="alert-banner alert-warning" role="status" aria-live="polite">
          <AlertTriangle size={20} aria-hidden />
          <span>Créditos baixos! Restam apenas <strong>{formatCredits(stats.availableCredits)}</strong> créditos.</span>
          {isAdminOrFinOps ? (
            <Link to={hasActiveStripeSub(org) ? '/pagamento' : '/assinar'} className="alert-action">
              {hasActiveStripeSub(org) ? 'Comprar créditos' : 'Assinar um plano'} <ArrowRight size={16} aria-hidden />
            </Link>
          ) : (
            <Link to="/solicitacoes" className="alert-action">Solicitar créditos <ArrowRight size={16} aria-hidden /></Link>
          )}
        </div>
      )}

      <div className="stats-grid">
        <div className="stat-card stat-primary">
          <div className="stat-icon"><Coins size={24} /></div>
          <div className="stat-info">
            <span className="stat-label">Créditos Disponíveis</span>
            <span className="stat-value">{formatCredits(stats.availableCredits)}</span>
          </div>
        </div>

        {isAdminOrFinOps && (
          <div className="stat-card stat-orange">
            <div className="stat-icon"><TrendingUp size={24} /></div>
            <div className="stat-info">
              <span className="stat-label">Gastos do Mês</span>
              <span className="stat-value">{formatCredits(stats.usedCreditsMonth)}</span>
            </div>
          </div>
        )}

        <div className="stat-card stat-green">
          <div className="stat-icon"><Video size={24} /></div>
          <div className="stat-info">
            <span className="stat-label">Total de Gerações</span>
            <span className="stat-value">{stats.totalGenerations}</span>
          </div>
        </div>

        {isAdminOrFinOps && (
          <div className="stat-card stat-yellow">
            <div className="stat-icon"><FileText size={24} /></div>
            <div className="stat-info">
              <span className="stat-label">Solicitações Pendentes</span>
              <span className="stat-value">{stats.pendingRequests}</span>
            </div>
          </div>
        )}
      </div>

      {isAdminOrFinOps && stats.dailyUsage.length > 0 && (
        <div className="glass-card wide-card chart-card">
          <h2>Consumo de créditos — últimos {CHART_DAYS} dias</h2>
          {totalChartCredits === 0 ? (
            <EmptyState
              flat
              icon={<BarChart3 size={24} />}
              title="Nenhum crédito gasto neste período"
              text="Quando sua equipe gerar imagens ou vídeos, o consumo diário aparece aqui."
            />
          ) : (
          <div
            className="chart-wrapper"
            role="img"
            aria-label={`Gráfico de barras do consumo diário. Total de ${formatCredits(totalChartCredits)} créditos nos últimos ${CHART_DAYS} dias.`}
          >
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={stats.dailyUsage} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                <XAxis dataKey="date" tick={{ fill: '#94a3b8', fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis tick={{ fill: '#94a3b8', fontSize: 11 }} />
                <Tooltip
                  contentStyle={{ background: '#1a1c23', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: '#fff' }}
                  labelStyle={{ color: '#94a3b8' }}
                  formatter={(value) => [formatCredits(Number(value) || 0), 'Créditos']}
                />
                <Bar dataKey="credits" fill="#6366f1" radius={[4, 4, 0, 0]} name="Créditos" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          )}
        </div>
      )}

      {stats.activeJobs.length > 0 && (
        <div className="glass-card wide-card" aria-live="polite">
          <div className="section-header">
            <h2>Em andamento</h2>
            <Link to="/galeria" className="btn-link">Abrir galeria <ArrowRight size={16} aria-hidden /></Link>
          </div>
          <div className="recent-list">
            {stats.activeJobs.map((job) => (
              <div key={job.id} className="recent-item">
                <div className="recent-thumb">
                  <Clock size={20} aria-hidden />
                </div>
                <div className="recent-info">
                  <span className="recent-prompt">
                    {job.media_type === 'video' ? 'Vídeo' : 'Imagem'} · {job.modality} · {job.provider}
                  </span>
                  <span className="recent-date">
                    {job.created_at ? new Date(job.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''}
                  </span>
                </div>
                <span className="badge badge-warning">{jobStatusLabel(job)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="glass-card wide-card">
        <div className="section-header">
          <h2>Últimas gerações</h2>
          {stats.recentGenerations.length > 0 && (
            <Link to="/galeria" className="btn-link">Ver todas na galeria <ArrowRight size={16} aria-hidden /></Link>
          )}
        </div>

        {stats.recentGenerations.length === 0 ? (
          <EmptyState
            flat
            icon={<Video size={24} />}
            title="Nenhuma geração ainda"
            text="Suas imagens e vídeos aparecem aqui assim que ficarem prontos. Veja o que os modelos fazem antes de gastar créditos."
            actions={
              <Link to="/explorar" className="explore-btn">
                <Compass size={16} aria-hidden /> Explorar modelos
              </Link>
            }
          />
        ) : (
          <div className="recent-list">
            {stats.recentGenerations.map((gen) => (
              <div key={gen.id} className="recent-item">
                <div className="recent-thumb">
                  {gen.original_image ? (
                    <img src={gen.original_image} alt="" loading="lazy" />
                  ) : (
                    <Video size={20} />
                  )}
                </div>
                <div className="recent-info">
                  <span className="recent-prompt">{gen.prompt.substring(0, 80)}{gen.prompt.length > 80 ? '...' : ''}</span>
                  <span className="recent-date">{gen.created_at ? new Date(gen.created_at).toLocaleDateString('pt-BR') : ''}</span>
                </div>
                <span className={`badge badge-${gen.status === 'completed' ? 'success' : gen.status === 'error' ? 'error' : 'warning'}`}>
                  {gen.status === 'completed' ? 'Concluído' : gen.status === 'error' ? 'Erro' : gen.status === 'pending' ? 'Pendente' : gen.status}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
